import React, { useMemo } from 'react';
import type { IconProps } from '@oneaudi/unified-web-components';
import { Text, Badge, Icon } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import type { Department } from '../../interfaces/header-components.interfaces';
import {
  getWeekDay,
  findOpeningHoursForDay,
  timeFormatted,
  isServiceOpen,
  getNextOpenDay,
  isBeforeOpening,
  getLocalizedTime,
  getLocalizedWeekdayName,
  getCurrentBusinessHours,
} from '../../utils/time-utils';
import { upperCase } from '../../utils/string-utils';
import { DealerDetailStyled, OpenHoursStyled, StyledLayout } from './DealerDetailStyles';
import { messages } from '../../i18n/messages';

type DepartmentOpeningHoursProps = {
  department: Department;
  icon?: IconProps['name'];
};
export const DepartmentOpeningHours: React.FC<DepartmentOpeningHoursProps> = ({
  icon = 'time',
  department,
}) => {
  const { openingHours, departmentName } = department;
  const today = useMemo(() => getWeekDay(), []);
  const openingHoursToday = findOpeningHoursForDay(openingHours, today);
  const timeRange = getCurrentBusinessHours(openingHoursToday?.timeRanges || []);
  const openTime = timeFormatted(timeRange.openTime);
  const closeTime = timeFormatted(timeRange.closeTime);
  const isOpen = isServiceOpen(openTime, closeTime);
  const opensSoon = isBeforeOpening(openTime);
  const next =
    !isOpen && !opensSoon
      ? getNextOpenDay(openingHours, openingHoursToday || openingHours[0])
      : openingHours[0];
  const openFragment = useI18n(messages.open_until);
  const closedFragment = useI18n(messages.closed_fragment);
  const todayFragment = useI18n(messages.today);
  const timeFormat = useI18n(messages.time_format);

  /* Get locale of user from browser navigator.
     Navigator is set here to prevent SSR errors
   */
  const userLocale =
    navigator?.languages && navigator?.languages?.length
      ? navigator?.languages[0]
      : navigator?.language;

  let statusText;
  if (isOpen) {
    statusText = (
      <Text>
        {openFragment} {getLocalizedTime(closeTime, timeFormat, userLocale)}
      </Text>
    );
  } else if (!opensSoon) {
    statusText = (
      <Text>
        {closedFragment} {upperCase(getLocalizedWeekdayName(next?.id, userLocale))} {` `}
        {getLocalizedTime(next?.timeRanges[0]?.openTime, timeFormat, userLocale)}
      </Text>
    );
  } else {
    statusText = (
      <Text>
        {closedFragment} {todayFragment} {getLocalizedTime(openTime, timeFormat)}
      </Text>
    );
  }

  return (
    <DealerDetailStyled>
      <StyledLayout align="center" className="icon">
        <Icon name={icon} size="s" />
      </StyledLayout>
      <StyledLayout>
        <Text>{departmentName}</Text>
        <OpenHoursStyled>
          <Badge className="badge" semanticShape={isOpen ? 'positive' : 'informative'} />
          {statusText}
        </OpenHoursStyled>
      </StyledLayout>
    </DealerDetailStyled>
  );
};
